/**
 * Toast utility functions - Bridge to Notification Center
 *
 * This file provides backward compatibility with the Sonner toast API
 * while routing notifications through the unified Notification Center.
 *
 * For new code, prefer using the useNotify hook from NotificationCenterContext:
 * ```tsx
 * import { useNotify } from '@/contexts/NotificationCenterContext';
 * const { notify } = useNotify();
 * notify.success('Saved!');
 * ```
 *
 * This bridge exists to support the ~50 existing files that use toast.*
 * without requiring a massive refactor.
 */

import { createElement, type MouseEvent } from 'react';
import { toast as sonnerToast } from 'sonner';
import { cn } from '@/lib/utils';

type ToastOptions = Exclude<
  Parameters<typeof sonnerToast.success>[1],
  undefined
>;

const TOAST_DEDUPE_WINDOW_MS = 4000;
const recentToastKeys = new Map<
  string,
  { expiresAt: number; id: string | number }
>();

function normalizeToastText(message?: string): string | undefined {
  if (!message) return undefined;

  return message.replace(/\s+/g, ' ').trim().replace(/[!]+$/g, '');
}

function resolveToastOptions(
  descriptionOrOptions?: string | ToastOptions
): ToastOptions {
  if (!descriptionOrOptions) {
    return {};
  }

  if (typeof descriptionOrOptions === 'string') {
    return { description: normalizeToastText(descriptionOrOptions) };
  }

  return {
    ...descriptionOrOptions,
    description:
      typeof descriptionOrOptions.description === 'string'
        ? normalizeToastText(descriptionOrOptions.description)
        : descriptionOrOptions.description,
  };
}

function withBaseToastOptions(
  descriptionOrOptions: string | ToastOptions | undefined,
  classNames: NonNullable<ToastOptions['classNames']>
): ToastOptions {
  const options = resolveToastOptions(descriptionOrOptions);

  return {
    ...options,
    className: cn('group', options.className),
    classNames: {
      ...classNames,
      ...options.classNames,
    },
  };
}

function buildToastDedupeKey(
  type: 'success' | 'error' | 'warning' | 'info' | 'loading',
  message: string,
  options: ToastOptions
): string | null {
  if (options.id !== undefined && options.id !== null) {
    return null;
  }

  const title = normalizeToastText(message) || message;
  const description =
    typeof options.description === 'string'
      ? normalizeToastText(options.description) || options.description
      : '';

  return `${type}:${title}:${description}`;
}

function pruneRecentToasts(now: number) {
  for (const [key, value] of recentToastKeys.entries()) {
    if (value.expiresAt <= now) {
      recentToastKeys.delete(key);
    }
  }
}

function emitToast(
  type: 'success' | 'error' | 'warning' | 'info' | 'loading',
  message: string,
  options: ToastOptions,
  emit: () => string | number
) {
  const now = Date.now();
  pruneRecentToasts(now);

  const dedupeKey = buildToastDedupeKey(type, message, options);
  if (dedupeKey) {
    const recent = recentToastKeys.get(dedupeKey);
    if (recent && recent.expiresAt > now) {
      return recent.id;
    }
  }

  const id = emit();

  if (dedupeKey) {
    recentToastKeys.set(dedupeKey, {
      expiresAt: now + TOAST_DEDUPE_WINDOW_MS,
      id,
    });
  }

  return id;
}

type ToastTone = 'success' | 'error' | 'warning' | 'info' | 'loading';
type ToastClassNames = NonNullable<ToastOptions['classNames']>;

// Sonner's unlayered `[data-title] { color: inherit }` beats Tailwind's layered
// utilities, so title colors need `!`. The `-strong` shades keep titles readable.
const toneClassNames: Record<ToastTone, ToastClassNames> = {
  success: {
    toast:
      'group-[.toaster]:bg-surface group-[.toaster]:border-success-500/20 group-[.toaster]:shadow-elevation-1',
    title: 'text-(--success-strong)! font-medium',
    description: 'group-[.toaster]:text-text-2',
    icon: 'group-[.toaster]:text-success-500',
  },
  error: {
    toast:
      'group-[.toaster]:bg-surface group-[.toaster]:border-danger-500/20 group-[.toaster]:shadow-elevation-1',
    title: 'text-(--danger-strong)! font-medium',
    description: 'group-[.toaster]:text-text-2',
    icon: 'group-[.toaster]:text-danger-500',
  },
  warning: {
    toast:
      'group-[.toaster]:bg-surface group-[.toaster]:border-warning-500/20 group-[.toaster]:shadow-elevation-1',
    title: 'text-(--warning-strong)! font-medium',
    description: 'group-[.toaster]:text-text-2',
    icon: 'group-[.toaster]:text-warning-500',
  },
  info: {
    toast:
      'group-[.toaster]:bg-surface group-[.toaster]:border-info-500/20 group-[.toaster]:shadow-elevation-1',
    title: 'text-(--info-strong)! font-medium',
    description: 'group-[.toaster]:text-text-2',
    icon: 'group-[.toaster]:text-info-500',
  },
  loading: {
    toast:
      'group-[.toaster]:bg-surface group-[.toaster]:border-border group-[.toaster]:shadow-elevation-1',
    title: 'group-[.toaster]:text-text-1 font-medium',
    description: 'group-[.toaster]:text-text-2',
    icon: 'group-[.toaster]:text-accent-500',
  },
};

/** Options for `toast.undo`. */
export interface UndoToastOptions {
  /** What just happened, such as "Expense deleted". */
  message: string;
  /** Optional second line, such as the record's name. */
  description?: string;
  /** Visible action text. Defaults to "Undo". */
  undoLabel?: string;
  /** Accessible name for the action, such as "Undo delete expense". Defaults to `undoLabel`. */
  undoAriaLabel?: string;
  /** Restores the record. Reject (or throw) when the restore fails. Runs at most once. */
  onUndo: () => Promise<unknown> | void;
  /** Shown while `onUndo` runs. Defaults to "Restoring". */
  undoingMessage?: string;
  /** Shown when `onUndo` resolves, such as "Expense restored". */
  undoneMessage: string;
  /** Shown when `onUndo` rejects. Defaults to "Couldn't restore. Try again from the trash." */
  undoFailedMessage?: string;
  /** Milliseconds the Undo stays available. Defaults to 8000. */
  duration?: number;
}

const UNDO_TOAST_DURATION_MS = 8000;
const UNDO_RESULT_DURATION_MS = 4000;
const UNDO_FAILED_DURATION_MS = 6000;
const DEFAULT_UNDO_FAILED_MESSAGE =
  "Couldn't restore. Try again from the trash.";
let undoToastSeq = 0;

/**
 * Undo toasts morph in place, so each phase gets a keyed line: a new phase
 * remounts it and the line eases in instead of snapping. Reduce Motion turns
 * the entrance off through the global rule in app/globals.css.
 */
const UNDO_LINE_CLASS =
  'block animate-in fade-in-0 duration-200 ease-out motion-safe:slide-in-from-bottom-1 motion-safe:blur-in-[2px]';
/** The icon swaps element per tone, so the new glyph grows in. */
const UNDO_ICON_CLASS =
  '[&>svg]:animate-in [&>svg]:fade-in-0 [&>svg]:duration-200 motion-safe:[&>svg]:zoom-in-75';
/**
 * Sonner snaps a toast to its new height when its content changes. The morph
 * eases the old height into the new one, using Arc's smooth spring timing as
 * a curve. Reduce Motion keeps the snap.
 */
const UNDO_MORPH_MS = 320;
const UNDO_MORPH_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

function morphToastHeight(node: HTMLElement | null, update: () => void) {
  const from = node?.isConnected ? node.getBoundingClientRect().height : 0;
  update();
  if (
    !node ||
    !from ||
    typeof node.animate !== 'function' ||
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }
  // Sonner commits each update from a zero timeout. This one queues behind it,
  // so the new height is measurable and the animation starts before a paint.
  setTimeout(() => {
    if (!node.isConnected) return;
    const to = node.getBoundingClientRect().height;
    if (Math.abs(to - from) < 1) return;
    node.animate([{ height: `${from}px` }, { height: `${to}px` }], {
      duration: UNDO_MORPH_MS,
      easing: UNDO_MORPH_EASE,
    });
  }, 0);
}

const UNDO_ACTION_CLASS = cn(
  'ml-auto inline-flex h-7 shrink-0 cursor-pointer items-center rounded-full border border-border bg-card px-3',
  'text-[0.8125rem] font-medium whitespace-nowrap text-foreground',
  'transition-[background-color,border-color,scale] duration-150 ease-out',
  'hover:border-border-strong hover:bg-muted active:scale-[0.98]',
  'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-popover'
);

function undoToastOptions(
  tone: ToastTone,
  id: string,
  extra: ToastOptions
): ToastOptions {
  const classNames = toneClassNames[tone];
  return {
    ...extra,
    id,
    className: 'group',
    classNames: {
      ...classNames,
      icon: cn(classNames.icon, UNDO_ICON_CLASS),
    },
  };
}

function undoLine(phase: string, text: string) {
  return createElement(
    'span',
    { key: phase, className: UNDO_LINE_CLASS },
    normalizeToastText(text) || text
  );
}

function focusVisible(element: Element) {
  try {
    return element.matches(':focus-visible');
  } catch {
    return document.activeElement === element;
  }
}

function showUndoToast({
  message,
  description,
  undoLabel = 'Undo',
  undoAriaLabel,
  onUndo,
  undoingMessage = 'Restoring',
  undoneMessage,
  undoFailedMessage = DEFAULT_UNDO_FAILED_MESSAGE,
  duration = UNDO_TOAST_DURATION_MS,
}: UndoToastOptions): string {
  undoToastSeq += 1;
  const id = `eclipse-undo-${undoToastSeq}`;
  let used = false;
  let node: HTMLElement | null = null;

  const settle = (tone: 'success' | 'error', text: string) => {
    const emit = tone === 'success' ? sonnerToast.success : sonnerToast.error;
    morphToastHeight(node, () =>
      emit(
        undoLine(tone, text),
        undoToastOptions(tone, id, {
          description: undefined,
          action: undefined,
          duration:
            tone === 'success'
              ? UNDO_RESULT_DURATION_MS
              : UNDO_FAILED_DURATION_MS,
        })
      )
    );
  };

  const runUndo = (event: MouseEvent) => {
    if (used) return;
    used = true;

    // The action folds away with this update; keep keyboard focus in the toast.
    const button = event.currentTarget as HTMLElement | null;
    node = button?.closest<HTMLElement>('[data-sonner-toast]') ?? null;
    if (button && focusVisible(button)) node?.focus();

    morphToastHeight(node, () =>
      sonnerToast.loading(
        undoLine('loading', undoingMessage),
        undoToastOptions('loading', id, {
          description: undefined,
          action: undefined,
          duration: Infinity,
        })
      )
    );

    let result: Promise<unknown> | void;
    try {
      result = onUndo();
    } catch {
      settle('error', undoFailedMessage);
      return;
    }
    Promise.resolve(result).then(
      () => settle('success', undoneMessage),
      () => settle('error', undoFailedMessage)
    );
  };

  sonnerToast.success(
    undoLine('initial', message),
    undoToastOptions('success', id, {
      description: normalizeToastText(description),
      duration,
      action: createElement(
        'button',
        {
          type: 'button',
          className: UNDO_ACTION_CLASS,
          'aria-label': undoAriaLabel ?? undoLabel,
          onClick: runUndo,
        },
        undoLabel
      ),
    })
  );

  return id;
}

// We still use Sonner under the hood because:
// 1. It handles the animation/positioning well
// 2. The NotificationCenterProvider might not be available in all contexts
// 3. Gradual migration is safer than a big-bang rewrite
//
// The toast styling matches Eclipse's theme and the notification center's style.

/**
 * Toast utility functions using Sonner
 * Preserves Eclipse Accounting's warm graphite theme
 */
export const toast = {
  /**
   * Success toast - uses success-500 color
   */
  success: (message: string, descriptionOrOptions?: string | ToastOptions) => {
    const options = withBaseToastOptions(
      descriptionOrOptions,
      toneClassNames.success
    );

    return emitToast('success', message, options, () =>
      sonnerToast.success(normalizeToastText(message) || message, options)
    );
  },

  /**
   * Error toast - uses danger-500 color
   */
  error: (message: string, descriptionOrOptions?: string | ToastOptions) => {
    const options = withBaseToastOptions(
      descriptionOrOptions,
      toneClassNames.error
    );

    return emitToast('error', message, options, () =>
      sonnerToast.error(normalizeToastText(message) || message, options)
    );
  },

  /**
   * Warning toast - uses warning-500 color
   */
  warning: (message: string, descriptionOrOptions?: string | ToastOptions) => {
    const options = withBaseToastOptions(
      descriptionOrOptions,
      toneClassNames.warning
    );

    return emitToast('warning', message, options, () =>
      sonnerToast.warning(normalizeToastText(message) || message, options)
    );
  },

  /**
   * Info toast - uses info-500 color
   */
  info: (message: string, descriptionOrOptions?: string | ToastOptions) => {
    const options = withBaseToastOptions(
      descriptionOrOptions,
      toneClassNames.info
    );

    return emitToast('info', message, options, () =>
      sonnerToast.info(normalizeToastText(message) || message, options)
    );
  },

  /**
   * Loading toast - uses accent-500 color
   */
  loading: (message: string, descriptionOrOptions?: string | ToastOptions) => {
    const options = withBaseToastOptions(
      descriptionOrOptions,
      toneClassNames.loading
    );

    return emitToast('loading', message, options, () =>
      sonnerToast.loading(normalizeToastText(message) || message, options)
    );
  },

  /**
   * Promise toast - automatically shows loading, success, or error
   */
  promise: <T>(
    promise: Promise<T>,
    {
      loading,
      success,
      error,
    }: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((err: unknown) => string);
    }
  ) => {
    return sonnerToast.promise(promise, {
      loading,
      success,
      error,
      classNames: {
        toast:
          'group-[.toaster]:bg-surface group-[.toaster]:border-border group-[.toaster]:shadow-elevation-1',
        title: 'font-medium',
        description: 'group-[.toaster]:text-text-2',
      },
    });
  },

  /**
   * Undo toast for a delete or other reversible change whose control is gone,
   * such as a menu delete or a row that unmounts. Pressing Undo morphs the
   * same toast to "Restoring", then to `undoneMessage` or the failure text.
   * The action runs `onUndo` once. Undo toasts are never deduplicated.
   * Returns the toast id.
   *
   * @example
   * toast.undo({
   *   message: 'Expense deleted',
   *   undoAriaLabel: 'Undo delete expense',
   *   onUndo: () => restoreExpense(id),
   *   undoneMessage: 'Expense restored',
   * });
   */
  undo: (options: UndoToastOptions) => showUndoToast(options),

  /**
   * Dismiss a specific toast
   */
  dismiss: (id?: string | number) => {
    sonnerToast.dismiss(id);
  },
};
